import { faCheckToSlot, faClipboardQuestion, faUser, faUsers, faUserTie } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../custom-hooks/redux/redux";
import useAuthorization, { isTabAuthorized } from "../../custom-hooks/useAuthorization/useAuthorization";

import useQuery from "../../custom-hooks/useQuery/useQuery";
import { Panel } from "../../reusable-components/MultipleTabs/interfaces";
import MultipleTabs from "../../reusable-components/MultipleTabs/MultipleTabs";
import { AUTHORIZATIONS } from "../../utils/constants/authorization-constants";
import { RecruitingLinks } from "../../widgetLink";
import Candidates from "./Candidates/Candidates";
import HiringClients from "./HiringClients/HiringClients";
import Interviews from "./Interviews/Interviews";
import Positions from "./Positions/Positions";
import Recruiters from "./Recruiters/Recruiters";

export enum ACTIVE_HIRING_CLIENT_ROUTES {
  OPEN_POSITIONS = "open-positions",
  CANDIDATES = "candidates",
  INTERVIEWS = "interviews",
  HIRING_CLIENTS = "hiring-clients",
  RECRUITERS = "recruiters",
}

const Recruiting = () => {
  const { t } = useTranslation();
  const tRP = "components.widgets.recruiting"; // tRP - translation root path
  const translationFields = `${tRP}.tables`;

  const query = useQuery();

  const { currentUserAuthorizations, currentUserData } = useAppSelector((state) => state.currentUserReducer);

  const hasAccess = useAuthorization([
    AUTHORIZATIONS.RecruitingAccess.CanAddHiringClients,
    AUTHORIZATIONS.RecruitingAccess.CanOpenPositionsAndAddCreateCandidates,
  ]);

  const checkPanelAccess = useCallback((showPanel: boolean, panel: Panel) => (showPanel ? [panel] : []), []);

  const allowPositions =
    isTabAuthorized(RecruitingLinks.Positions, currentUserAuthorizations, currentUserData) || hasAccess;

  const allowHiringClients =
    isTabAuthorized(RecruitingLinks.HiringClients, currentUserAuthorizations, currentUserData) || hasAccess;

  const allowCandidates =
    isTabAuthorized(RecruitingLinks.Candidates, currentUserAuthorizations, currentUserData) || hasAccess;

  const panels: Panel[] = useMemo(
    () => [
      ...checkPanelAccess(allowPositions, {
        tabLabel: t(`${translationFields}.open_positions`),
        tabPanelComponent: <Positions />,
        tabIcon: <FontAwesomeIcon icon={faCheckToSlot} />,
        routePath: ACTIVE_HIRING_CLIENT_ROUTES.OPEN_POSITIONS,
      }),
      ...checkPanelAccess(allowCandidates, {
        tabLabel: t(`${translationFields}.candidates`),
        tabPanelComponent: <Candidates />,
        tabIcon: <FontAwesomeIcon icon={faUser} />,
        routePath: ACTIVE_HIRING_CLIENT_ROUTES.CANDIDATES,
      }),
      ...checkPanelAccess(hasAccess, {
        tabLabel: t(`${translationFields}.interviews`),
        tabPanelComponent: <Interviews />,
        tabIcon: <FontAwesomeIcon icon={faClipboardQuestion} />,
        routePath: ACTIVE_HIRING_CLIENT_ROUTES.INTERVIEWS,
      }),
      ...checkPanelAccess(allowHiringClients, {
        tabLabel: t(`${translationFields}.hirirng_clients`),
        tabPanelComponent: <HiringClients />,
        tabIcon: <FontAwesomeIcon icon={faUsers} />,
        routePath: ACTIVE_HIRING_CLIENT_ROUTES.HIRING_CLIENTS,
      }),
      ...checkPanelAccess(hasAccess, {
        tabLabel: t(`${translationFields}.recruiters`),
        tabPanelComponent: <Recruiters />,
        tabIcon: <FontAwesomeIcon icon={faUserTie} />,
        routePath: ACTIVE_HIRING_CLIENT_ROUTES.RECRUITERS,
      }),
    ],
    [t]
  );

  const selectedTab = useMemo(() => {
    const foundIndex = panels.findIndex((panel) => panel.routePath === query.get("tab"));
    return foundIndex >= 0 ? foundIndex : 0;
  }, [query, panels]);

  return (
    <MultipleTabs
      panels={panels}
      selectedTab={selectedTab}
    />
  );
};

export default Recruiting;
